import { createContext, useContext, useMemo, useState } from "react";
import { useActorUser, setActorUserCache } from "@-/dev.improve.haniverse.lib.user";
import {
  createCustomLevelPack,
  customLevelPacksSchema,
  EMPTY_CUSTOM_LEVEL_PACKS,
  type CustomLevelPack,
} from "@-/dev.improve.haniverse.lib.level.custom";
import { useSaveCustomLevelPack } from "#use-save-custom-level-pack";

export type CustomEditorPanel = "tools" | "pack" | "level" | "cards" | "copy" | "slide";
export type CustomEditorSelection = { itemId?: string; levelId: string } | null;

export function useCustomLevelsState() {
  const user = useActorUser();
  const parsed = useMemo(
    () => customLevelPacksSchema.safeParse(user.customLevelPacks ?? EMPTY_CUSTOM_LEVEL_PACKS),
    [user.customLevelPacks],
  );
  const packs = parsed.success ? parsed.data.packs : [];
  const [isCustom, setIsCustom] = useState(false);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CustomLevelPack | null>(null);
  const [original, setOriginal] = useState<CustomLevelPack | null>(null);
  const [selection, setSelection] = useState<CustomEditorSelection>(null);
  const [panel, setPanel] = useState<CustomEditorPanel>("tools");
  const [sheetOpen, setSheetOpen] = useState(false);
  const saveMutation = useSaveCustomLevelPack();
  const pack = draft ?? packs.find((entry) => entry.id === selectedPackId) ?? packs[0] ?? null;
  const openPanel = (next: CustomEditorPanel) => {
    setPanel(next);
    setSheetOpen(true);
  };
  const beginEdit = (next: CustomLevelPack, previous: CustomLevelPack | null) => {
    saveMutation.reset();
    setOriginal(previous);
    setDraft(next);
    setSelection(next.levels[0] ? { levelId: next.levels[0].id } : null);
    openPanel(previous === null ? "pack" : "tools");
  };
  const cancel = () => {
    if (saveMutation.isPending) {
      return;
    }
    setDraft(null);
    setOriginal(null);
    setSelection(null);
    setSheetOpen(false);
    saveMutation.reset();
  };
  const save = () => {
    if (draft === null || saveMutation.isPending) {
      return;
    }
    saveMutation.mutate(
      { original, pack: draft },
      {
        onError: () => openPanel("tools"),
        onSuccess: (updatedUser) => {
          setActorUserCache(updatedUser);
          setSelectedPackId(draft.id);
          setDraft(null);
          setOriginal(null);
          setSelection(null);
          setSheetOpen(false);
        },
      },
    );
  };
  return {
    cancel,
    create: () => beginEdit(createCustomLevelPack(user.hanziScript), null),
    documentError: parsed.success
      ? null
      : "These packs use an unsupported or invalid format. Existing data has been kept.",
    edit: () => {
      if (pack !== null) {
        beginEdit(pack, pack);
      }
    },
    isCustom,
    isEditing: draft !== null,
    isSaving: saveMutation.isPending,
    openPanel,
    pack,
    packs,
    panel,
    save,
    saveError: saveMutation.error?.message ?? null,
    selection,
    selectPack: (id: string) => {
      if (draft === null) {
        setSelectedPackId(id);
      }
    },
    setIsCustom,
    setSelection,
    setSheetOpen,
    sheetOpen,
    updateDraft: (update: (current: CustomLevelPack) => CustomLevelPack) => {
      if (!saveMutation.isPending) {
        setDraft((current) => (current === null ? null : update(current)));
      }
    },
  };
}

export const CustomLevelsContext = createContext<ReturnType<typeof useCustomLevelsState> | null>(
  null,
);
export function useCustomLevelsContext() {
  const context = useContext(CustomLevelsContext);
  if (context === null) {
    throw new Error("CustomLevelsProvider is missing.");
  }
  return context;
}
